import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cadenceApi, messageSendsApi } from '../services/api';
import { Cadence, SendStatsRow } from '../types';
import { pct } from '../lib';
import { Dash, Empty, Failed, Loading, PageFrame, Pill, Section, Th, td, trClick } from '../components/PlatformBits';

/** Cadences — one row per cadence: who is in it now and what its steps have
 *  led to, all time. A row opens the cadence's own page: its delivery board
 *  and the steps that went out. */

const num = (n: number) => n.toLocaleString('en-IN');

const CadencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cadences, setCadences] = useState<Cadence[] | null>(null);
  const [byStep, setByStep] = useState<SendStatsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([cadenceApi.list(), messageSendsApi.stats('step', { producer: 'cadence' })])
      .then(([c, s]) => { setCadences(c); setByStep(s); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, []);

  // Ledger numbers per cadence, through step id → cadence.
  const totals = useMemo(() => {
    const stepToCadence: Record<string, string> = {};
    for (const c of cadences || []) for (const st of c.steps) stepToCadence[st.id] = c.id;
    const out: Record<string, { sends: number; delivered: number; read: number; replied: number; visited: number; failed: number }> = {};
    for (const r of byStep || []) {
      const cid = stepToCadence[r.key || ''];
      if (!cid) continue;
      const t = out[cid] || (out[cid] = { sends: 0, delivered: 0, read: 0, replied: 0, visited: 0, failed: 0 });
      t.sends += r.sends; t.delivered += r.delivered; t.read += r.read; t.replied += r.replied; t.visited += r.visited; t.failed += r.failed;
    }
    return out;
  }, [cadences, byStep]);

  const rows = useMemo(() => (cadences || []).slice().sort((a, b) => b.enrolled - a.enrolled || b.updated_at.localeCompare(a.updated_at)), [cadences]);

  if (error) return <Failed what="cadences" error={error} />;

  return (
    <PageFrame title="Cadences" subline="Every cadence, who is in it now, and what its steps have led to — open one for its board.">
      {!cadences || !byStep ? <Loading what="cadences" /> : (
        <Section title={`${rows.length} cadence${rows.length === 1 ? '' : 's'}`} note="numbers are all time · visits booked within 14 days of a step">
          <table className="w-full min-w-[860px] border-collapse">
            <thead><tr>
              <Th left>Cadence</Th><Th>Steps</Th><Th>In it now</Th><Th>Completed</Th><Th ours>Visits booked</Th><Th ours>Replied</Th><Th>Steps sent</Th><Th>Delivered</Th><Th>Read</Th><Th>Failed</Th>
              <th className="w-6 border-b border-charcoal/10" />
            </tr></thead>
            <tbody>
              {rows.length === 0 && <Empty cols={11}>No cadences yet.</Empty>}
              {rows.map((c) => {
                const n = totals[c.id];
                return (
                  <tr key={c.id} onClick={() => navigate(`/cadences/${c.id}`)} className={`${trClick(false)} group !align-middle`}>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-[14px] text-charcoal group-hover:text-forest">{c.name}</span>
                      {c.held > 0 && <span className="ml-2"><Pill tone="gold">{c.held} held</Pill></span>}
                    </td>
                    <td className={`${td} text-right`}>{c.steps.length}</td>
                    <td className={`${td} text-right`}><span className={c.enrolled ? 'font-semibold text-forest text-[14px]' : ''}>{num(c.enrolled)}</span></td>
                    <td className={`${td} text-right text-graphite`}>{num(c.completed)}</td>
                    {n ? (
                      <>
                        <td className={`${td} text-right`}>{n.visited ? <span className="text-forest font-semibold text-[15px]">{num(n.visited)}</span> : <Dash />}</td>
                        <td className={`${td} text-right`}>
                          <span className="font-semibold text-[14px] text-amber-ink">{num(n.replied)}</span>
                          <span className="text-[11.5px] text-graphite ml-1">{pct(n.replied, n.sends)}</span>
                        </td>
                        <td className={`${td} text-right font-semibold text-[14px]`}>{num(n.sends)}</td>
                        <td className={`${td} text-right text-graphite`}>{pct(n.delivered, n.sends)}</td>
                        <td className={`${td} text-right text-graphite`}>{pct(n.read, n.sends)}</td>
                        <td className={`${td} text-right ${n.failed ? 'text-brick font-semibold' : 'text-graphite'}`}>{n.failed ? num(n.failed) : <Dash />}</td>
                      </>
                    ) : (
                      <td colSpan={6} className="px-3 py-3 text-right text-[12.5px] text-graphite">no sends on the ledger</td>
                    )}
                    <td className="pr-3 text-right text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}
    </PageFrame>
  );
};

export default CadencesPage;
